import { useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Badge,
  Chip,
  InputAdornment,
  Button,
  Collapse,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import api from '@/services/auth.service';
import { useAuth } from '@/hooks/useAuth';
import { useProdutosFotosBatch } from '@/hooks/useProdutoFotos';
import { usePedidoStore } from '../../stores/pedido.store';
import { CatalogoProduto, type ProdutoCatalogo } from '../CatalogoProduto';
import { CatalogoArvore } from '../CatalogoArvore';
import { useCatalogoModo } from '../../hooks/useCatalogoModo';
import {
  montarArvore,
  resolverCaminho,
  type NivelArvore,
  type NoCatalogoApi
} from '../../services/catalogo-arvore';

// AIDEV-NOTE: Resposta da API /api/produtos
interface ProdutosApiResponse {
  produtos: ProdutoCatalogo[];
  total?: number;
  secoes?: string[];
  grupos?: string[];
  subgrupos?: string[];
}

// AIDEV-NOTE: Step 2 do wizard de pedido - catalogo de produtos com filtros,
// busca e lista virtualizada via @tanstack/react-virtual.

// AIDEV-NOTE: Identidade da linha = produto (mesma tripla usada no carrinho), NUNCA o indice.
// O default do virtualizer chaveia por indice: ao trocar a busca o React reaproveitava a
// instancia do card e o preco/quantidade digitados vazavam para o produto seguinte.
function getProdutoKey(p: ProdutoCatalogo): string {
  return `${p.CODIGO}-${p.CODCOR ?? 0}-${p.CODTAMANHO ?? 0}`;
}

export function Step2Catalogo() {
  const { user } = useAuth();
  const itens = usePedidoStore((s) => s.itens);
  const clienteSelecionado = usePedidoStore((s) => s.clienteSelecionado);
  const tabelaPrecoId = usePedidoStore((s) => s.tabelaPrecoId);
  const setTabelaPreco = usePedidoStore((s) => s.setTabelaPreco);

  // AIDEV-NOTE: Filtros no store -> persistem ao navegar entre steps (Step2 desmonta no carrinho).
  // Limpos automaticamente no resetPedido (finalizar/novo pedido).
  const { busca, secao, grupo, subgrupo, filtrosAbertos, caminhoArvore } = usePedidoStore(
    (s) => s.filtrosCatalogo
  );
  const setFiltrosCatalogo = usePedidoStore((s) => s.setFiltrosCatalogo);

  // AIDEV-NOTE: Duas formas de ver o catalogo: 'lista' (busca + dropdowns) e 'grupos'
  // (navegacao Secao > Grupo > Subgrupo). A busca sempre vence: digitou, sai da arvore e
  // procura no catalogo inteiro; limpou, volta para o ponto onde estava.
  const [modo, setModo] = useCatalogoModo();

  // AIDEV-NOTE: Debounce da busca - usamos o valor diretamente no queryKey
  // e TanStack Query faz o dedup/cache. keepPreviousData evita flash.
  const buscaTrimmed = busca.trim();
  const buscando = buscaTrimmed.length > 0;
  const modoArvore = modo === 'grupos' && !buscando;

  const { data: arvoreResponse, isLoading: carregandoArvore } = useQuery({
    queryKey: ['produtos-arvore'],
    // AIDEV-PERF: a arvore so muda quando o sync traz grupo novo — 5 min de cache evita
    // um GROUP BY no catalogo inteiro a cada abertura do Step 2.
    staleTime: 5 * 60_000,
    enabled: modo === 'grupos',
    queryFn: async (): Promise<{ nos: NoCatalogoApi[] }> => {
      const res = await api.get<{ nos: NoCatalogoApi[] }>('/produtos/arvore');
      return res.data;
    }
  });

  const arvore = useMemo(() => montarArvore(arvoreResponse?.nos ?? []), [arvoreResponse]);
  const { trilha, nos: nosDoNivel } = useMemo(
    () => resolverCaminho(arvore, caminhoArvore),
    [arvore, caminhoArvore]
  );

  // AIDEV-NOTE: Em modo arvore quem manda nos filtros e o caminho; os dropdowns ficam ocultos
  // para nao existirem duas fontes de verdade. Valor '' no caminho e o balde "sem classificacao"
  // e vira sem_<nivel>=1 — mandar grupo='' listaria o catalogo inteiro (a API ignora vazio).
  const filtrosProduto = useMemo(() => {
    if (!modoArvore) {
      return { secao, grupo, subgrupo, vazios: [] as NivelArvore[] };
    }
    const alvo = { secao: '', grupo: '', subgrupo: '', vazios: [] as NivelArvore[] };
    caminhoArvore.forEach((valor, idx) => {
      const nivel = arvore.niveis[idx];
      if (!nivel) return;
      if (valor === '') alvo.vazios.push(nivel);
      else alvo[nivel] = valor;
    });
    return alvo;
  }, [modoArvore, secao, grupo, subgrupo, caminhoArvore, arvore.niveis]);

  // Folha = caminho escolhido que nao tem mais filhos: hora de listar produtos
  const emFolha = modoArvore && caminhoArvore.length > 0 && nosDoNivel.length === 0;
  const mostrarProdutos = !modoArvore || emFolha;

  const {
    data: response,
    isLoading,
    error
  } = useQuery({
    queryKey: [
      'produtos-catalogo',
      buscaTrimmed,
      filtrosProduto.secao,
      filtrosProduto.grupo,
      filtrosProduto.subgrupo,
      filtrosProduto.vazios.join(','),
      tabelaPrecoId
    ],
    staleTime: 60_000,
    // AIDEV-PERF: navegando entre grupos nao ha lista para buscar — so no nivel folha
    enabled: mostrarProdutos,
    placeholderData: (previousData) => previousData,
    queryFn: async (): Promise<ProdutosApiResponse> => {
      const params: Record<string, string | number> = { limit: 200, offset: 0 };
      if (buscaTrimmed) params.busca = buscaTrimmed;
      if (filtrosProduto.secao) params.secao = filtrosProduto.secao;
      if (filtrosProduto.grupo) params.grupo = filtrosProduto.grupo;
      if (filtrosProduto.subgrupo) params.subgrupo = filtrosProduto.subgrupo;
      for (const nivel of filtrosProduto.vazios) params[`sem_${nivel}`] = 1;
      if (tabelaPrecoId != null) params.tabela_preco_id = tabelaPrecoId;

      const res = await api.get<ProdutosApiResponse>('/produtos', { params });
      return res.data;
    }
  });

  // AIDEV-PERF: memoizado — sem isso o `?? []` cria array novo a cada render e derruba a
  // memoizacao de produtoCodigos/getItemKey (getItemKey instavel refaz opcoes do virtualizer).
  const produtos = useMemo(() => response?.produtos ?? [], [response]);
  const secoes = response?.secoes ?? [];
  const grupos = response?.grupos ?? [];
  const subgrupos = response?.subgrupos ?? [];

  // AIDEV-PERF: Batch query para thumbnails — uma unica request para todos os codigos da pagina
  const produtoCodigos = useMemo(() => produtos.map((p) => p.CODIGO), [produtos]);
  const { data: fotosMap } = useProdutosFotosBatch(produtoCodigos);

  // AIDEV-NOTE: Lista virtualizada para performance com muitos produtos
  const parentRef = useRef<HTMLDivElement>(null);

  const getItemKey = useCallback(
    (index: number) => {
      const p = produtos[index];
      return p ? getProdutoKey(p) : index;
    },
    [produtos]
  );

  const rowVirtualizer = useVirtualizer({
    count: produtos.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120,
    overscan: 5,
    getItemKey
  });

  // AIDEV-NOTE: Filtro de tabela de preco so aparece se vendedor.pode_selecionar_tabela
  const podeSelecionarTabela = user?.pode_selecionar_tabela ?? false;
  const temFiltroAtivo = !!(secao || grupo || subgrupo || (podeSelecionarTabela && tabelaPrecoId));

  const totalItensCarrinho = useMemo(
    () => itens.reduce((sum, item) => sum + item.quantidade, 0),
    [itens]
  );

  const handleNavegarArvore = useCallback(
    (caminho: string[]) => setFiltrosCatalogo({ caminhoArvore: caminho }),
    [setFiltrosCatalogo]
  );

  const handleBuscaChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFiltrosCatalogo({ busca: e.target.value });
    },
    [setFiltrosCatalogo]
  );

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 3 },
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden'
      }}
    >
      {/* Header com info do cliente e carrinho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" noWrap>
            Catalogo de Produtos
          </Typography>
          {clienteSelecionado && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {clienteSelecionado.RAZAO_SOCIAL}
            </Typography>
          )}
        </Box>
        <Badge badgeContent={totalItensCarrinho} color="primary" sx={{ ml: 1, flexShrink: 0 }}>
          <Chip
            icon={<CartIcon />}
            label={`${totalItensCarrinho}`}
            variant="outlined"
            color="primary"
            size="small"
          />
        </Badge>
      </Box>

      {/* AIDEV-NOTE: Busca sempre visivel; filtros adicionais colapsaveis no mobile */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
        <TextField
          fullWidth
          size="small"
          label="Buscar produto"
          placeholder="Nome, codigo ou referencia..."
          value={busca}
          onChange={handleBuscaChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
        />
        <Button
          size="small"
          aria-label="Filtros"
          variant={temFiltroAtivo ? 'contained' : 'outlined'}
          onClick={() => setFiltrosCatalogo({ filtrosAbertos: !filtrosAbertos })}
          sx={{ minWidth: 0, px: 1.5, flexShrink: 0 }}
        >
          <FilterListIcon fontSize="small" />
        </Button>
      </Box>

      {/* Filtros adicionais — colapsaveis via botao filtro */}
      <Collapse in={filtrosAbertos}>
        {/* AIDEV-NOTE: alternador das duas formas de visualizacao — dentro do painel de filtros */}
        <ToggleButtonGroup
          exclusive
          size="small"
          value={modo}
          onChange={(_, valor) => valor && setModo(valor as typeof modo)}
          sx={{ mb: 1 }}
        >
          <ToggleButton value="lista" sx={{ textTransform: 'none', px: 2 }}>
            Lista
          </ToggleButton>
          <ToggleButton value="grupos" sx={{ textTransform: 'none', px: 2 }}>
            Grupos
          </ToggleButton>
        </ToggleButtonGroup>

        <Grid container spacing={1} sx={{ mb: 1.5 }}>
          {modo === 'lista' && (
            <>
              <Grid item xs={6} sm={podeSelecionarTabela ? 2 : 3}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Secao"
                  value={secao}
                  onChange={(e) => setFiltrosCatalogo({ secao: e.target.value })}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {secoes.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={podeSelecionarTabela ? 2 : 3}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Grupo"
                  value={grupo}
                  onChange={(e) => setFiltrosCatalogo({ grupo: e.target.value })}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {grupos.map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={podeSelecionarTabela ? 2 : 3}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Subgrupo"
                  value={subgrupo}
                  onChange={(e) => setFiltrosCatalogo({ subgrupo: e.target.value })}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {subgrupos.map((sg) => (
                    <MenuItem key={sg} value={sg}>
                      {sg}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </>
          )}
          {/* AIDEV-NOTE: Seletor de tabela de preco - so visivel se vendedor.pode_selecionar_tabela */}
          {podeSelecionarTabela && (
            <Grid item xs={12} sm={4}>
              <TabelaPrecoSelect value={tabelaPrecoId} onChange={setTabelaPreco} />
            </Grid>
          )}
        </Grid>
      </Collapse>

      {/* Estado de erro */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erro ao carregar produtos. Tente novamente.
        </Alert>
      )}

      {/* Navegacao por grupos — breadcrumb sempre, nos enquanto nao chega na folha */}
      {modoArvore && !carregandoArvore && (
        <CatalogoArvore
          nos={nosDoNivel}
          trilha={trilha}
          caminho={caminhoArvore}
          onNavegar={handleNavegarArvore}
        />
      )}

      {/* Loading */}
      {(isLoading || (modoArvore && carregandoArvore)) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Lista virtualizada de produtos */}
      {mostrarProdutos && !isLoading && produtos.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">Nenhum produto encontrado.</Typography>
        </Box>
      )}

      {mostrarProdutos && !isLoading && produtos.length > 0 && (
        <Box
          ref={parentRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            // AIDEV-NOTE: overscrollBehavior:contain isola o scroll da lista de produtos —
            // impede que o scroll vaze para o MainLayout ao atingir o topo/fundo da lista,
            // evitando o problema de "ora scrola a tela, ora os produtos" no mobile.
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <Box
            sx={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative'
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const produto = produtos[virtualRow.index] as ProdutoCatalogo | undefined;
              if (!produto) return null;
              return (
                <Box
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                >
                  <CatalogoProduto
                    produto={produto}
                    thumbnailUrl={fotosMap?.[String(produto.CODIGO)]?.[0]}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// AIDEV-NOTE: Componente auxiliar para selecionar tabela de preco.
// Busca as tabelas disponiveis via query separada.

interface TabelaPrecoSelectProps {
  value: number | null;
  onChange: (id: number | null) => void;
}

interface TabelaPrecoOption {
  id: number;
  descricao: string;
}

function TabelaPrecoSelect({ value, onChange }: TabelaPrecoSelectProps) {
  const { data: tabelas = [] } = useQuery({
    queryKey: ['tabelas-preco'],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<TabelaPrecoOption[]> => {
      const res = await api.get<{ tabelas: TabelaPrecoOption[] }>('/tabelas-preco');
      return res.data.tabelas;
    }
  });

  return (
    <TextField
      fullWidth
      size="small"
      select
      label="Tabela de Preco"
      value={value ?? ''}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '' ? null : Number(v));
      }}
    >
      <MenuItem value="">Padrao</MenuItem>
      {tabelas.map((t) => (
        <MenuItem key={t.id} value={t.id}>
          {t.descricao}
        </MenuItem>
      ))}
    </TextField>
  );
}
